import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { referralsAPI } from "../lib/api";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { ArrowRight, Gift, Sparkles } from "lucide-react";

/**
 * Lightweight dashboard teaser. The full share UI lives at /refer.
 * Auto-hides when the program is disabled or set to 0% so nothing dangles.
 */
const ReferralCard = () => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        try {
            const r = await referralsAPI.me();
            setData(r.data);
        } catch {
            // Non-critical — card stays hidden
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    if (loading || !data?.code) return null;
    if (!data.program_enabled || (data.discount_percent || 0) <= 0) return null;

    const pct = data.discount_percent;

    return (
        <Card
            className="border-primary/20 bg-gradient-to-br from-primary/[0.04] to-accent/[0.06] overflow-hidden"
            data-testid="referral-card"
        >
            <CardContent className="p-5">
                <div className="flex items-start gap-3 mb-4">
                    <div className="h-9 w-9 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <Gift className="h-4 w-4 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <p className="text-[10px] uppercase tracking-widest font-semibold text-primary mb-0.5">
                            Refer · Earn
                        </p>
                        <p className="font-serif text-lg leading-tight" data-testid="referral-card-headline">
                            Give {pct}% off.<br />Get {pct}% off.
                        </p>
                    </div>
                </div>

                <p className="text-xs text-muted-foreground leading-relaxed mb-4">
                    Send your link. When a friend signs up, you both unlock <span className="font-semibold text-foreground">{pct}% off your next plan</span>.
                </p>

                <Link to="/refer" className="block">
                    <Button
                        className="w-full rounded-full btn-press group"
                        data-testid="referral-card-cta"
                    >
                        Open share page
                        <ArrowRight className="h-4 w-4 ml-1.5 group-hover:translate-x-0.5 transition-transform" />
                    </Button>
                </Link>

                {data.has_pending_discount && (
                    <p className="text-[11px] text-accent text-center font-medium mt-3 flex items-center justify-center gap-1" data-testid="referral-discount-ready">
                        <Sparkles className="h-3 w-3" />
                        Your {pct}% discount is queued for checkout
                    </p>
                )}
            </CardContent>
        </Card>
    );
};

export default ReferralCard;
